/**
 * AssignTicketModal – Select an eligible admin user to assign a support request to.
 *
 * Features:
 * - Fetches active SuperAdmin / SupportAdmin users
 * - Searchable list with name + role badge
 * - Highlights the currently-assigned user
 * - "Unassign" option to clear the assignment
 * - Loading and error states
 */
import { useState, useEffect, useMemo } from 'react';
import { Button } from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@respectly/ui';
import { Input } from '@respectly/ui';
import { cn } from '@respectly/ui';
import {
  UserPlus,
  UserX,
  Search,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type { SuperAdminUserListItem } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface AssignTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentAssignedToId?: string | null;
  onAssign: (userId: string | null) => Promise<boolean>;
  isAssigning?: boolean;
}

// Roles eligible to be assigned support tickets
const ELIGIBLE_ROLES: SuperAdminUserListItem['role'][] = ['SuperAdmin', 'SupportAdmin'];

const ROLE_LABELS: Record<string, string> = {
  SuperAdmin: 'Super Admin',
  SupportAdmin: 'Support Admin',
  BillingAdmin: 'Billing Admin',
  ComplianceAdmin: 'Compliance Admin',
  ReadOnlyAdmin: 'Read Only',
};

const ROLE_COLORS: Record<string, string> = {
  SuperAdmin: 'bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400',
  SupportAdmin: 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400',
};

// ============================================
// Component
// ============================================

export function AssignTicketModal({
  isOpen,
  onClose,
  currentAssignedToId,
  onAssign,
  isAssigning = false,
}: AssignTicketModalProps) {
  const [users, setUsers] = useState<SuperAdminUserListItem[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Track whether the user has made a selection; use a separate "dirty" flag
  // so null (Unassign) is distinguishable from "nothing selected yet".
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    currentAssignedToId ?? null
  );
  const [hasMadeSelection, setHasMadeSelection] = useState(false);

  // Reset selection state when the modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUserId(currentAssignedToId ?? null);
      setHasMadeSelection(false);
      setSearch('');
      setLoadError(null);
      setAssignError(null);
    }
  }, [isOpen, currentAssignedToId]);

  // Fetch eligible admin users when the modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      setLoadError(null);
      try {
        const api = getApiClient();
        // Fetch active users with a generous limit; filter eligible roles client-side
        const params = new URLSearchParams({ limit: '200', isActive: 'true' });
        const response = await api.get<{
          data: SuperAdminUserListItem[];
        }>(`${API_ENDPOINTS.SUPER_ADMIN.USERS.LIST}?${params.toString()}`);
        setUsers(
          (response.data ?? []).filter((u) =>
            ELIGIBLE_ROLES.includes(u.role as SuperAdminUserListItem['role'])
          )
        );
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load admin users');
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [isOpen]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.firstName.toLowerCase().includes(q) ||
        u.lastName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  const handleSelect = (userId: string | null) => {
    setSelectedUserId(userId);
    setHasMadeSelection(true);
    setAssignError(null);
  };

  const handleConfirm = async () => {
    if (!hasMadeSelection) return;
    setAssignError(null);
    const success = await onAssign(selectedUserId);
    if (success) {
      onClose();
    } else {
      setAssignError('Failed to assign the ticket. Please try again.');
    }
  };

  const hasChanged = hasMadeSelection && selectedUserId !== (currentAssignedToId ?? null);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-500" />
            Assign Ticket
          </DialogTitle>
          <DialogDescription>
            Choose an admin to assign this support request to.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search admins…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
              autoFocus
            />
          </div>

          {/* User list */}
          <div className="max-h-64 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-10 gap-2 text-sm text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading admins…
              </div>
            ) : loadError ? (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {loadError}
              </div>
            ) : (
              <>
                {/* Unassign option */}
                <button
                  type="button"
                  onClick={() => handleSelect(null)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors border-b border-zinc-100 dark:border-zinc-800',
                    selectedUserId === null && hasMadeSelection
                      ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-50'
                      : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-900'
                  )}
                >
                  <span className="h-7 w-7 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center shrink-0">
                    <UserX className="h-3.5 w-3.5 text-zinc-500" />
                  </span>
                  <span className="flex-1 text-left">Unassigned</span>
                  {selectedUserId === null && hasMadeSelection && (
                    <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0" />
                  )}
                </button>

                {/* Admin users */}
                {filteredUsers.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-zinc-400 text-center">
                    No admins found
                  </div>
                ) : (
                  filteredUsers.map((user) => {
                    const isSelected = selectedUserId === user.id;
                    return (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleSelect(user.id)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                          isSelected
                            ? 'bg-blue-50 dark:bg-blue-950/30 text-zinc-900 dark:text-zinc-50'
                            : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-900'
                        )}
                      >
                        {/* Avatar */}
                        <span className="h-7 w-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-[11px] font-semibold shrink-0 uppercase">
                          {user.firstName[0]}{user.lastName[0]}
                        </span>

                        {/* Name + email */}
                        <span className="flex-1 text-left min-w-0">
                          <span className="block font-medium truncate">
                            {user.firstName} {user.lastName}
                          </span>
                          <span className="block text-xs text-zinc-400 truncate">{user.email}</span>
                        </span>

                        {/* Role badge */}
                        <span
                          className={cn(
                            'shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
                            ROLE_COLORS[user.role] ?? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500'
                          )}
                        >
                          {ROLE_LABELS[user.role] ?? user.role}
                        </span>

                        {isSelected && (
                          <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
          {assignError && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 sm:mr-auto">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{assignError}</span>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isAssigning}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isAssigning || !hasChanged || isLoadingUsers}
            >
              {isAssigning ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Assigning…
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {selectedUserId === null ? 'Unassign' : 'Assign'}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
